"use client";

// useSpringValue — map a raw, jumpy signal (scroll offset, pointer position,
// a target scalar) to a weighted, inertial value that lags and settles.
//
// This is the core primitive behind innernet's "spring-driven, not
// mouse-registration" feel. You push a raw target in with the returned setter
// (or `set(...)`); the hook runs it through framer-motion's `useSpring` so what
// you read out has momentum and over-damped settle (per the SPRINGS presets).
//
// It is intentionally NOT a render-on-every-frame value. To keep the existing
// throttled / threshold-driven consumers cheap, the smoothed value is delivered
// through an `onChange` callback (the underlying MotionValue.on("change", …)),
// NOT React state. The caller decides what to do with each frame (e.g. forward
// it to a context setter only past a threshold) — exactly the discipline the
// scroll→age consumers already use, now fed a sprung signal instead of the raw
// one. If you DO want a reactive number, use `useSpringScalar` below.
//
// Reduced motion: when the user prefers reduced motion the spring is bypassed
// entirely — `set()` calls the change handler synchronously with the raw value,
// so behaviour collapses to the original direct mapping. No physics, no lag.

import { useEffect, useRef } from "react";
import { useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { spring, type SpringName } from "./springs";
import type { SpringOptions } from "framer-motion";

export type SpringValueOptions = {
  /** Spring preset name or raw options. Default: "gentle". */
  preset?: SpringName | SpringOptions;
  /** Initial raw value. Default: 0. */
  initial?: number;
  /** Called with each smoothed frame value (and synchronously when reduced). */
  onChange?: (value: number) => void;
};

export type SpringValueHandle = {
  /** Push a new raw target. The output springs toward it (or snaps, if reduced). */
  set: (target: number) => void;
  /** Jump output instantly to a value (no animation) — e.g. to re-sync. */
  jump: (value: number) => void;
  /** Read the current smoothed value. */
  get: () => number;
};

/**
 * Callback-style sprung scalar. Returns a stable handle; the onChange fires on
 * every spring frame. Prefer this when the consumer throttles/thresholds the
 * value itself (it never triggers a React re-render on its own).
 */
export function useSpringValue(opts: SpringValueOptions = {}): SpringValueHandle {
  const { preset = "gentle", initial = 0, onChange } = opts;
  const reduce = useReducedMotion();

  const raw = useMotionValue(initial);
  const smooth = useSpring(raw, spring(preset));

  // Keep the latest onChange without re-subscribing the MotionValue each render.
  const cb = useRef(onChange);
  cb.current = onChange;

  const reduceRef = useRef(reduce);
  reduceRef.current = reduce;

  // Subscribe once to the smoothed value; forward every frame to the caller.
  useEffect(() => {
    const unsub = smooth.on("change", (v) => cb.current?.(v));
    return unsub;
  }, [smooth]);

  const handle = useRef<SpringValueHandle>({
    set: (target) => {
      if (reduceRef.current) {
        // No physics: snap the spring to the target and report it immediately.
        smooth.jump(target);
        cb.current?.(target);
      } else {
        raw.set(target);
      }
    },
    jump: (value) => {
      raw.jump(value);
      smooth.jump(value);
      cb.current?.(value);
    },
    get: () => smooth.get(),
  });

  return handle.current;
}
